import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  AdjustmentsHorizontalIcon, 
  PencilIcon, 
  TrashIcon, 
  ArrowDownIcon, 
  ArrowUpIcon,
  ArrowsUpDownIcon,
  EyeIcon,
  StarIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../utils/supabase';
import { updateProduct, inventoryApi,productsApi } from '../../utils/api';
import FacebookExportModal from './FacebookExportModal';
// Indicateur d'activation de l'intégration Shopify (REACT_APP_ENABLE_SHOPIFY)
import { SHOPIFY_ENABLED } from '../../config/features';
import { getDeviseActive } from '../../utils/devise';

const ProductList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Récupérer la page initiale depuis le state de navigation (si retour depuis ProductFormPage)
  const initialPage = location.state?.initialPage || 1;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const [showFacebookExportModal, setShowFacebookExportModal] = useState(false);

  // États pour la génération de SKU/codes-barres
  const [generatingCodesProductId, setGeneratingCodesProductId] = useState(null);
  const [codeGenerationResults, setCodeGenerationResults] = useState(null);
  const [showCodeGenerationModal, setShowCodeGenerationModal] = useState(false);

  // États pour la resynchronisation Shopify
  const [syncingProductId, setSyncingProductId] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);

  // États pour la pagination
  // Initialiser avec la page de retour si disponible (depuis ProductFormPage)
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [pageSize, setPageSize] = useState(30); // Ou un autre nombre souhaité

  // État pour le champ de saisie de page (pour éviter les requêtes immédiates)
  const [pageInputValue, setPageInputValue] = useState(initialPage.toString());

  // Charger les produits depuis l'API backend
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm,
        // Note: 'filter' ici est pour le statut de stock (low, out) géré par getProductsForStock.
        // Si ProductList a besoin de filtrer par statut de produit (active, draft), 
        // cela nécessitera un paramètre différent ou une modification du backend.
        // Pour l'instant, nous n'envoyons pas selectedStatus ou selectedCategory au backend 
        // car getProductsForStock n'est pas conçu pour eux.
        // Le filtrage par category/status se fera côté client pour l'instant.
      };

      // Utiliser productApi.getProductsForCatalog qui est déjà configuré pour la pagination
      const response = await productsApi.getProductsForCatalog(params);

      if (response && response.data) {
        // Les produits sont formatés par le backend pour le contexte de StockManagement.
        // Il faudra peut-être ajuster le formatage si ProductList a des besoins différents
        // ou créer une route API dédiée pour ProductList.
        // Pour l'instant, on utilise les données telles quelles.
        setProducts(response.data.products || []);
        setTotalProducts(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 0);
      } else {
        throw new Error("Réponse invalide de l'API");
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setError(error.response?.data?.message || error.message || 'Erreur de chargement des produits.');
      setProducts([]);
      setTotalProducts(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Nettoyer le state de navigation après l'avoir utilisé pour éviter qu'il persiste
  useEffect(() => {
    if (location.state?.initialPage) {
      // Remplacer le state actuel sans l'initialPage pour éviter sa persistance
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, pageSize]); // Dépendances pour recharger

  // Synchroniser pageInputValue avec currentPage
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Gérer le changement de page
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Gérer le changement du champ de saisie avec délai (debounce)
  useEffect(() => {
    // Attendre 1 seconde après la dernière saisie avant de changer de page
    const timeoutId = setTimeout(() => {
      const pageNumber = parseInt(pageInputValue, 10);
      if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== currentPage) {
        handlePageChange(pageNumber);
      }
    }, 1000); // Délai de 1 seconde

    // Nettoyer le timeout si l'utilisateur continue de taper
    return () => clearTimeout(timeoutId);
  }, [pageInputValue, totalPages, currentPage]);

  // Filtrage des produits (côté client pour selectedCategory et selectedStatus pour l'instant)
  const filteredProducts = useMemo(() => {
    let productsToFilter = [...products]; // products vient maintenant de l'API (déjà filtré par searchTerm backend)
    
    if (selectedCategory) {
      productsToFilter = productsToFilter.filter(product => product.category === selectedCategory);
    }
    if (selectedStatus) {
      // Assumons que les produits de l'API ont un champ 'status' correspondant (active, draft, etc.)
      // Si le format du backend pour `getProductsForStock` ne retourne pas ce statut de produit,
      // il faudra l'ajouter au backend ou le déduire différemment.
      productsToFilter = productsToFilter.filter(product => product.status_product === selectedStatus); // Supposons un champ 'status_product'
    }
    return productsToFilter;
  }, [products, selectedCategory, selectedStatus]);

  // Tri des produits
  const sortedProducts = useMemo(() => {
    let sortableItems = [...filteredProducts];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredProducts, sortConfig]);

  // Liste des catégories uniques
  const categories = useMemo(() => {
    const uniqueCategories = new Set(products.map(product => product.category));
    return ['', ...Array.from(uniqueCategories)];
  }, [products]);

  // Gestion du tri
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Rendu des icones de tri
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 text-primary-500" />
      : <ArrowDownIcon className="h-4 w-4 text-primary-500" />;
  };

  // Fonction utilitaire pour tronquer le texte
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Suppression d'un produit
  const handleDeleteProduct = async (id) => {
    try {
      // Supprimer d'abord les variants liés au produit
      const { error: variantsError } = await supabase
        .from('product_variants')
        .delete()
        .eq('product_id', id);
      
      if (variantsError) throw variantsError;
      
      // Puis supprimer le produit
      const { error: productError } = await supabase
        .from('products')
        .delete()
        .eq('id', id);
      
      if (productError) throw productError;
      
      // Mettre à jour l'état local
      setProducts(products.filter(product => product.id !== id));
      setShowDeleteConfirm(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Erreur lors de la suppression du produit:', error);
      // Afficher un message d'erreur à l'utilisateur
      alert(`Erreur: ${error.message}`);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // --- Fonction pour l'édition ---
  /**
   * Navigation vers la page d'édition d'un produit
   * Supporte l'ouverture dans un nouvel onglet avec Alt+Click
   * @param {Object} product - Le produit à éditer
   * @param {Event} event - L'événement de clic (pour détecter Alt)
   */
  const handleEditProduct = (product, event) => {
    console.log("Editing product:", product);

    // Construire l'URL de destination
    const url = `/app/products/${product.id}/edit`;

    // Si la touche Alt est maintenue, ouvrir dans un nouvel onglet
    if (event?.altKey) {
      // Créer un élément <a> temporaire pour garantir l'ouverture dans un onglet
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Navigation normale avec contexte de navigation (state)
      // Récupérer la liste des IDs des produits affichés pour la navigation entre produits
      const productIds = sortedProducts.map(p => p.id);
      const currentIndex = productIds.indexOf(product.id);

      navigate(url, {
        state: {
          returnPage: currentPage,
          productIds: productIds, // Liste des IDs pour navigation
          currentIndex: currentIndex, // Index du produit actuel dans la liste
          searchTerm: searchTerm, // Conserver les filtres pour cohérence
          selectedCategory: selectedCategory,
          selectedStatus: selectedStatus
        }
      });
    }
  };
  // --- Fin Fonction pour l'édition ---

  // --- Fonction pour les avis ---
  const handleViewReviews = (product) => {
    console.log("Viewing reviews for product:", product);
    // Naviguer vers la page des avis
    navigate(`/app/products/${product.id}/reviews`);
  };
  // --- Fin Fonction pour les avis ---

  // --- Fonction pour voir les commandes d'une variante ---
  /**
   * Navigation vers la page des commandes avec filtre sur une variante spécifique
   * Utilise des query params pour passer les informations de filtrage
   * Supporte l'ouverture dans un nouvel onglet avec Alt+Click
   * @param {Object} variant - L'objet variante sélectionné
   * @param {Object} product - L'objet produit parent
   * @param {Event} event - L'événement de clic (pour détecter Alt)
   */
  const handleViewVariantOrders = (variant, product, event) => {
    console.log("Viewing orders for variant:", variant, "of product:", product);

    // Construire l'URL de destination avec les query params pour le filtre
    const url = `/app/orders?tab=orders&variantId=${variant.id}&variantName=${encodeURIComponent(variant.name)}&productName=${encodeURIComponent(product.name)}`;

    // Si la touche Alt est maintenue, ouvrir dans un nouvel onglet
    if (event?.altKey) {
      // Créer un élément <a> temporaire pour garantir l'ouverture dans un onglet (pas une fenêtre)
      // Cette méthode est plus fiable que window.open() qui peut ouvrir une fenêtre
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer'; // Sécurité et performance
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Comportement normal : navigation dans le même onglet
      navigate(url);
    }
  };
  // --- Fin Fonction pour voir les commandes d'une variante ---

  // **NOUVELLE FONCTION : Générer les SKU et codes-barres**
  const handleGenerateSkuAndBarcode = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) {
      alert("Produit non trouvé.");
      return;
    }

    setGeneratingCodesProductId(productId);
    try {
      console.log('[ProductList] Génération SKU/Barcode pour produit:', productId);
      
      const response = await inventoryApi.generateSkuAndBarcode({ 
        productId: productId 
      });
      
      if (response && response.data && response.data.success) {
        const data = response.data.data;
        
        // Stocker les résultats pour affichage dans le modal
        setCodeGenerationResults({
          success: true,
          message: response.data.message,
          productName: product.name,
          data: data
        });
        setShowCodeGenerationModal(true);
        
        // Recharger les produits pour afficher les nouveaux codes
        await fetchProducts(); 
        
        console.log('[ProductList] Génération codes terminée:', data);
      } else {
        throw new Error(response?.data?.message || "La génération des codes a échoué. Réponse inattendue du serveur.");
      }
    } catch (error) {
      console.error(`[ProductList] Erreur lors de la génération des codes pour le produit ${productId}:`, error);
      
      // Stocker l'erreur pour affichage dans le modal
      setCodeGenerationResults({
        success: false,
        message: `Erreur de génération: ${error.message}`,
        productName: product.name,
        data: null
      });
      setShowCodeGenerationModal(true);
    } finally {
      setGeneratingCodesProductId(null);
    }
  };

  // **NOUVELLE FONCTION : Resynchroniser produit Shopify**
  const handleSyncShopifyProduct = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) {
      alert("Produit non trouvé.");
      return;
    }

    if (product.external_source !== 'shopify') {
      alert("Ce produit ne provient pas de Shopify.");
      return;
    }

    setSyncingProductId(productId);
    try {
      console.log('[ProductList] Resynchronisation Shopify pour produit:', productId);
      
      const response = await inventoryApi.syncShopifyProduct({ 
        productId: productId 
      });
      
      if (response && response.data && response.data.success) {
        const data = response.data.data;
        
        // Stocker les résultats pour affichage dans le modal
        setSyncResults({
          success: true,
          message: response.data.message,
          productName: product.name,
          data: data
        });
        setShowSyncModal(true);
        
        // Recharger les produits pour afficher les données mises à jour
        await fetchProducts(); 
        
        console.log('[ProductList] Resynchronisation terminée:', data);
      } else {
        throw new Error(response?.data?.message || "La resynchronisation a échoué. Réponse inattendue du serveur.");
      }
    } catch (error) {
      console.error(`[ProductList] Erreur lors de la resynchronisation du produit ${productId}:`, error);
      
      // Stocker l'erreur pour affichage dans le modal
      setSyncResults({
        success: false,
        message: `Erreur de resynchronisation: ${error.message}`,
        productName: product.name,
        data: null
      });
      setShowSyncModal(true);
    } finally {
      setSyncingProductId(null);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      {/* Barre de recherche et filtres - Version responsive */}
      <div className="p-3 sm:p-4 border-b border-gray-200">
        <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-9 sm:pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-xs sm:text-sm"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <select
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-xs sm:text-sm"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Toutes catégories</option>
              {categories.slice(1).map((category, index) => (
                <option key={index} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-xs sm:text-sm"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">Tous statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="draft">Brouillon</option>
            </select>
          </div>

          {/* Le bouton suit les accents privés ; la couleur de marque reste réservée aux logos. */}
          <div className="flex justify-end lg:col-span-3 xl:col-span-1 xl:justify-start">
             <button
               onClick={() => setShowFacebookExportModal(true)}
               className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto justify-center"
               title="Exporter catalogue Facebook"
             >
               <ArrowUpTrayIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
               Facebook CSV
             </button>
           </div>
        </div>
      </div>

      {/* Afficher un indicateur de chargement */}
      {loading && (
        <div className="p-6 sm:p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 sm:h-8 w-6 sm:w-8 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-2 text-xs sm:text-sm text-gray-500">Chargement des produits...</p>
        </div>
      )}
      
      {/* Afficher un message d'erreur */}
      {error && (
        <div className="p-6 sm:p-8 text-center text-red-500">
          <p className="text-xs sm:text-sm">Erreur: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-md text-xs sm:text-sm"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Tableau de produits - Version responsive */}
      {!loading && !error && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('name')}
                  >
                    Produit
                    <span className="ml-1 sm:ml-2">{getSortIcon('name')}</span>
                  </button>
                </th>
                <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('category')}
                  >
                    Catégorie
                    <span className="ml-1 sm:ml-2">{getSortIcon('category')}</span>
                  </button>
                </th>
                <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('price')}
                  >
                    Prix
                    <span className="ml-1 sm:ml-2">{getSortIcon('price')}</span>
                  </button>
                </th>
                <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('status')}
                  >
                    Statut
                    <span className="ml-1 sm:ml-2">{getSortIcon('status')}</span>
                  </button>
                </th>
                <th scope="col" className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                        <img className="h-8 w-8 sm:h-10 sm:w-10 rounded-md object-cover" src={product.mainImage} alt={product.name} />
                      </div>
                      <div className="ml-2 sm:ml-4 min-w-0 flex-1">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {window.innerWidth < 768 ? truncateText(product.name, 15) : truncateText(product.name)}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{product.sku}</div>
                        {(product.barcode || (product.variants && product.variants.length > 0 && product.variants[0].barcode)) && (
                          <div className="text-xs text-gray-500 truncate hidden sm:block">
                            {product.barcode || (product.variants && product.variants[0].barcode)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                    <div className="text-xs sm:text-sm text-gray-900">{product.category}</div>
                    <div className="text-xs text-gray-500">{product.subCategory}</div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    { product.discountPrice && product.discountPrice > 0 ? (
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900">{formatPrice(product.discountPrice)}</div>
                        <div className="text-xs text-gray-500 line-through">{formatPrice(product.price)}</div>
                        <div className="text-xs text-gray-500">{getDeviseActive()}</div>
                      </div>
                    ) : product.price && product.price > 0 ? (
                      <div>
                        <div className="text-xs sm:text-sm text-gray-900">{formatPrice(product.price)}</div>
                        <div className="text-xs text-gray-500">{getDeviseActive()}</div>
                      </div>
                    ) : product.variants && product.variants.length > 0 && product.variants[0].price > 0 ? (
                      <div>
                        <div className="text-xs sm:text-sm text-gray-900">{formatPrice(product.variants[0].price)}</div>
                        <div className="text-xs text-gray-500">{getDeviseActive()}</div>
                      </div>
                    ) : (
                      <span className="text-xs sm:text-sm text-gray-500">N/A</span>
                    )}
                  </td>

                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden md:table-cell">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${product.status === 'active' ? 'bg-green-100 text-green-800' : product.status === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {product.status === 'active' ? 'Actif' : product.status === 'inactive' ? 'Inactif' : 'Brouillon'}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-1 sm:space-x-2">
                      <button 
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowQuickView(true);
                        }}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        title="Voir les détails"
                      >
                        <EyeIcon className="h-4 sm:h-5 w-4 sm:w-5" aria-hidden="true" />
                      </button>
                      <button 
                        onClick={() => handleViewReviews(product)}
                        className="text-yellow-600 hover:text-yellow-700 p-1"
                        title="Gérer les avis"
                      >
                        <StarIcon className="h-4 sm:h-5 w-4 sm:w-5" aria-hidden="true" />
                      </button>
                      {window.innerWidth >= 640 && (
                        <button
                          onClick={() => handleGenerateSkuAndBarcode(product.id)}
                          disabled={generatingCodesProductId === product.id}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed p-1"
                          title="Générer les SKU et codes-barres"
                        >
                          {generatingCodesProductId === product.id ? (
                            <div className="animate-spin rounded-full h-4 sm:h-5 w-4 sm:w-5 border-t-2 border-b-2 border-green-600"></div>
                          ) : (
                            <svg className="h-4 sm:h-5 w-4 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a.997.997 0 01-1.414 0l-7-7A2 2 0 013 12V7a2 2 0 012-2z" />
                            </svg>
                          )}
                        </button>
                      )}
                      {/* Resynchronisation Shopify : masquée quand l'intégration est désactivée */}
                      {SHOPIFY_ENABLED && product.external_source === 'shopify' && window.innerWidth >= 640 && (
                        <button
                          onClick={() => handleSyncShopifyProduct(product.id)}
                          disabled={syncingProductId === product.id}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed p-1"
                          title="Resynchroniser avec Shopify"
                        >
                          {syncingProductId === product.id ? (
                            <div className="animate-spin rounded-full h-4 sm:h-5 w-4 sm:w-5 border-t-2 border-b-2 border-blue-600"></div>
                          ) : (
                            <svg className="h-4 sm:h-5 w-4 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          )}
                        </button>
                      )}
                      <button
                        onClick={(e) => handleEditProduct(product, e)}
                        className="text-primary-600 hover:text-primary-900 p-1"
                        title="Modifier le produit (Alt+Click pour nouvel onglet)"
                      >
                        <PencilIcon className="h-4 sm:h-5 w-4 sm:w-5" aria-hidden="true" />
                      </button>
                      {window.innerWidth >= 640 && (
                        <button 
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowDeleteConfirm(true);
                          }}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Supprimer le produit"
                        >
                          <TrashIcon className="h-4 sm:h-5 w-4 sm:w-5" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Message si aucun produit */}
          {sortedProducts.length === 0 && !loading && (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <p className="text-xs sm:text-sm">Aucun produit trouvé. Importez des produits depuis Shopify ou créez-en de nouveaux.</p>
            </div>
          )}

          {/* Contrôles de Pagination responsive */}
          {totalPages > 0 && (
            <div className="px-3 sm:px-6 py-3 border-t border-gray-200">
              {/* Version mobile */}
              <div className="flex items-center justify-between sm:hidden mb-3">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Précédent
                </button>
                <span className="text-xs text-gray-500 flex items-center">
                  {currentPage}/{totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>

              {/* Version desktop */}
              <div className="hidden sm:flex sm:items-center sm:justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{totalPages}</span> ({totalProducts} produits)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Précédent</span>
                      <ArrowDownIcon className="h-5 w-5 transform -rotate-90" aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Suivant</span>
                      <ArrowUpIcon className="h-5 w-5 transform -rotate-90" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>

              {/* Champ de saisie rapide de page (responsive) avec délai (debounce) */}
              <div className="flex items-center justify-center gap-2">
                <label htmlFor="page-input" className="text-xs sm:text-sm text-gray-600">
                  Aller à la page:
                </label>
                <input
                  id="page-input"
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInputValue}
                  onChange={(e) => {
                    // Mettre à jour uniquement la valeur du champ, la requête se fera après 1 seconde
                    setPageInputValue(e.target.value);
                  }}
                  className="w-16 sm:w-20 px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="N°"
                />
                <span className="text-xs sm:text-sm text-gray-500">/ {totalPages}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vue rapide du produit - Version responsive */}
      {showQuickView && selectedProduct && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg shadow-xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto ${window.innerWidth < 768 ? 'max-w-[95vw]' : 'max-w-2xl'}`}>
            <div className="flex justify-between items-start mb-4">
              <h3 className={`${window.innerWidth < 768 ? 'text-base' : 'text-lg'} font-medium text-gray-900 pr-4`}>
                {window.innerWidth < 768 ? truncateText(selectedProduct.name, 25) : truncateText(selectedProduct.name)}
              </h3>
              <button
                onClick={() => {
                  setShowQuickView(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 hover:text-gray-500 flex-shrink-0"
              >
                <span className="sr-only">Fermer</span>
                <svg className="h-5 sm:h-6 w-5 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <img className={`w-full ${window.innerWidth < 768 ? 'h-32' : 'h-48'} object-cover rounded-md`} src={selectedProduct.mainImage} alt={selectedProduct.name} />
                <div className="mt-3 sm:mt-4">
                  <p className="text-xs sm:text-sm text-gray-500">SKU: {selectedProduct.sku}</p>
                  <p className={`mt-1 ${window.innerWidth < 768 ? 'text-base' : 'text-lg'} font-semibold`}>
                    { selectedProduct.discountPrice ? (
                      <>
                        <span className="text-primary-600">{formatPrice(selectedProduct.discountPrice)} {getDeviseActive()}</span>
                        <span className="ml-2 text-gray-500 line-through">{formatPrice(selectedProduct.price)} {getDeviseActive()}</span>
                      </>
                    ) : (
                      <span>{formatPrice(selectedProduct.price)} {getDeviseActive()}</span>
                    )}
                  </p>
                </div>
              </div>
              <div>
                <h4 className={`font-medium ${window.innerWidth < 768 ? 'text-sm' : 'text-base'}`}>Détails</h4>
                <div className="mt-2 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs sm:text-sm text-gray-500">Catégorie:</span>
                    <span className="text-xs sm:text-sm text-right">{selectedProduct.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs sm:text-sm text-gray-500">Sous-catégorie:</span>
                    <span className="text-xs sm:text-sm text-right">{selectedProduct.subCategory}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-gray-500">Statut:</span>
                    <span className={`px-2 py-1 text-xs leading-4 font-semibold rounded-full ${selectedProduct.status === 'active' ? 'bg-green-100 text-green-800' : selectedProduct.status === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {selectedProduct.status === 'active' ? 'Actif' : selectedProduct.status === 'inactive' ? 'Inactif' : 'Brouillon'}
                    </span>
                  </div>
                </div>

                {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                  <div className="mt-4">
                    <h4 className={`font-medium ${window.innerWidth < 768 ? 'text-sm' : 'text-base'}`}>Variantes</h4>
                    <div className="mt-2 space-y-2">
                      {selectedProduct.variants.map(variation => (
                        <div key={variation.id} className="border rounded p-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs sm:text-sm truncate pr-2">{variation.name}</span>
                            <div className="flex items-center gap-2">
                              {/* Affichage du stock avec code couleur */}
                              <span className={`text-xs sm:text-sm font-medium ${
                                variation.stock <= 0 ? 'text-red-600' :
                                variation.stock < 3 ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>
                                {variation.stock}
                              </span>
                              {/* Bouton pour voir les commandes de cette variante */}
                              {/* Passer l'événement pour détecter Alt+Click et ouvrir dans un nouvel onglet */}
                              <button
                                onClick={(e) => handleViewVariantOrders(variation, selectedProduct, e)}
                                className="text-primary-600 hover:text-primary-900 p-1"
                                title="Voir les commandes de cette variante (Alt+Click pour nouvel onglet)"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowQuickView(false);
                  setSelectedProduct(null);
                }}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
              >
                Fermer
              </button>
              <button
                onClick={(e) => {
                  if (selectedProduct.external_source === 'local') {
                    handleEditProduct(selectedProduct, e);
                  }
                }}
                disabled={selectedProduct.external_source !== 'local'}
                className="px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                title={selectedProduct.external_source === 'local' ? 'Éditer le produit (Alt+Click pour nouvel onglet)' : 'Produit non éditable'}
              >
                {selectedProduct.external_source === 'local' ? 'Éditer' : 'Non éditable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression - Version responsive */}
      {showDeleteConfirm && selectedProduct && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg shadow-xl w-full p-4 sm:p-6 ${window.innerWidth < 768 ? 'max-w-[95vw]' : 'max-w-md'}`}>
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-red-100 sm:mx-0">
                <TrashIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className={`${window.innerWidth < 768 ? 'text-base' : 'text-lg'} leading-6 font-medium text-gray-900`}>
                  Supprimer le produit
                </h3>
                <div className="mt-2">
                  <p className="text-xs sm:text-sm text-gray-500">
                    Êtes-vous sûr de vouloir supprimer "{window.innerWidth < 768 ? truncateText(selectedProduct.name, 20) : truncateText(selectedProduct.name)}" ? Cette action ne peut pas être annulée.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row-reverse space-y-2 sm:space-y-0 sm:space-x-3 sm:space-x-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-3 sm:px-4 py-2 bg-red-600 text-xs sm:text-sm font-medium text-white hover:bg-red-700 focus:outline-none"
                onClick={() => handleDeleteProduct(selectedProduct.id)}
              >
                Supprimer
              </button>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-3 sm:px-4 py-2 bg-white text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedProduct(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Modal de résultats de génération de codes - Version responsive */}
      {showCodeGenerationModal && codeGenerationResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto ${window.innerWidth < 768 ? 'max-w-[95vw]' : 'max-w-2xl'}`}>
            {/* En-tête du modal */}
            <div className={`px-4 sm:px-6 py-4 border-b ${codeGenerationResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {codeGenerationResults.success ? (
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                  <h3 className={`ml-3 ${window.innerWidth < 768 ? 'text-base' : 'text-lg'} font-semibold ${codeGenerationResults.success ? 'text-green-900' : 'text-red-900'}`}>
                    {codeGenerationResults.success ? 'Codes générés !' : 'Erreur de génération'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowCodeGenerationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenu du modal */}
            <div className="px-4 sm:px-6 py-4">
              {codeGenerationResults.success && codeGenerationResults.data ? (
                <div className="space-y-6">
                  {/* Informations du produit */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📦 Produit traité</h4>
                    <p className="text-blue-800">{codeGenerationResults.productName}</p>
                  </div>

                  {/* Résumé principal */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {codeGenerationResults.data.totalUpdated || 0}
                      </div>
                      <div className="text-sm text-green-800">Éléments mis à jour</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {codeGenerationResults.data.updatedItems?.length || 0}
                      </div>
                      <div className="text-sm text-blue-800">Codes générés</div>
                    </div>
                  </div>

                  {/* Détails des éléments mis à jour */}
                  {codeGenerationResults.data.updatedItems && codeGenerationResults.data.updatedItems.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">🏷️ Codes générés</h4>
                      <div className="space-y-3">
                        {codeGenerationResults.data.updatedItems.map((item, index) => (
                          <div key={index} className="bg-white rounded-md p-3 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-900">
                                {item.type === 'product' ? '📦' : '🔗'} {item.name}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.type === 'product' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                              }`} data-ui-preserve-color={true /* Conserve le repère métier, graphique ou de marque. */} >
                                {item.type === 'product' ? 'Produit' : 'Variante'}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="font-medium text-gray-600">SKU:</span>
                                <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">{item.sku}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-600">Code-barres:</span>
                                <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">{item.barcode}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Erreurs */}
                  {codeGenerationResults.data.errors && codeGenerationResults.data.errors.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-2">
                        ⚠️ Erreurs ({codeGenerationResults.data.errors.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto">
                        {codeGenerationResults.data.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-700 mb-1">
                            • {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-red-600">{codeGenerationResults.message}</p>
                </div>
              )}
            </div>

            {/* Pied du modal */}
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCodeGenerationModal(false)}
                  className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 text-xs sm:text-sm w-full sm:w-auto"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de résultats de resynchronisation Shopify */}
      {showSyncModal && syncResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto ${window.innerWidth < 768 ? 'max-w-[95vw]' : 'max-w-2xl'}`}>
            {/* En-tête du modal */}
            <div className={`px-4 sm:px-6 py-4 border-b ${syncResults.success ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`} data-ui-preserve-color={true /* Conserve le repère métier, graphique ou de marque. */} >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {syncResults.success ? (
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                  <h3 className={`ml-3 ${window.innerWidth < 768 ? 'text-base' : 'text-lg'} font-semibold ${syncResults.success ? 'text-blue-900' : 'text-red-900'}`} data-ui-preserve-color={true /* Conserve le repère métier, graphique ou de marque. */} >
                    {syncResults.success ? 'Resynchronisation réussie' : 'Erreur de resynchronisation'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenu du modal */}
            <div className="px-4 sm:px-6 py-4">
              {syncResults.success && syncResults.data ? (
                <div className="space-y-6">
                  {/* Informations du produit */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📦 Produit resynchronisé</h4>
                    <p className="text-blue-800">{syncResults.productName}</p>
                  </div>

                  {/* Détails de la synchronisation */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-3">✅ Données mises à jour</h4>
                    <div className="space-y-2 text-sm text-green-800">
                      {syncResults.data.updatedFields && syncResults.data.updatedFields.map((field, index) => (
                        <div key={index}>• {field}</div>
                      ))}
                      {syncResults.data.imagesCount && (
                        <div>• {syncResults.data.imagesCount} image(s) synchronisée(s)</div>
                      )}
                      {syncResults.data.variantsCount && (
                        <div>• {syncResults.data.variantsCount} variante(s) synchronisée(s)</div>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="text-center py-4">
                    <p className="text-gray-600">{syncResults.message}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-red-600">{syncResults.message}</p>
                </div>
              )}
            </div>

            {/* Pied du modal */}
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 text-xs sm:text-sm w-full sm:w-auto"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export Facebook */}
      <FacebookExportModal 
        isOpen={showFacebookExportModal} 
        onClose={() => setShowFacebookExportModal(false)} 
      />
    </div>
  );
};

export default ProductList; 